"use client";

import { useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { EmpiricalResults } from "@/components/tools/chemistry/empirical-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  combustionMasses,
  type ElementMass,
  type EmpiricalError,
  type EmpiricalResult,
  empiricalFromMasses,
  molecularMultiple,
} from "@/lib/tool-kit/chemistry/empirical";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const EMPIRICAL_FORMULA_CALCULATOR_STRINGS = {
  modeLabel: "Start from",
  modeMasses: "Percent composition or masses",
  modeCombustion: "Combustion analysis",
  valuesAre: "The values are",
  valuesPercent: "Percentages",
  valuesGrams: "Masses in grams",
  rowsHeading: "Elements",
  symbolLabel: "Element symbol {number}",
  valueLabel: "Value for element {number}",
  addRow: "Add an element",
  removeRow: "Remove element {number}",
  exampleSymbols: ["C", "H", "O"],
  exampleValues: ["40.92", "4.58", "54.50"],
  sampleLabel: "Mass of sample (g)",
  co2Label: "Mass of CO₂ produced (g)",
  waterLabel: "Mass of H₂O produced (g)",
  restIsOxygen: "The rest of the sample is oxygen (found by difference)",
  combustionHint:
    "All the carbon ends up in CO₂ and all the hydrogen in H₂O. Other elements are not measured here.",
  molarMassLabel: "Molar mass of the compound (g/mol, optional)",
  molarMassHint:
    "Add it to turn the empirical formula into the molecular formula.",
  toleranceLabel: "How close to a whole number?",
  toleranceStrict: "Strict: within {value}",
  toleranceStandard: "Standard: within {value}",
  toleranceLoose: "Loose: within {value}",
  results: "Results",
  empirical: "Empirical formula",
  empiricalMass: "Empirical formula mass",
  molecular: "Molecular formula",
  stepsHeading: "From masses to a whole-number ratio",
  columnElement: "Element",
  columnWeight: "Atomic weight (g/mol)",
  columnMoles: "Moles",
  columnRatio: "÷ smallest",
  columnScaled: "× {k}",
  columnWhole: "Subscript",
  columnPercent: "Percent (%)",
  columnGrams: "Mass (g)",
  working: "The working",
  moleLine: "{symbol}: {mass} ÷ {weight} g/mol = {moles} mol",
  ratioLine: "Divide by the smallest ({smallest}): {ratios}",
  multiplyLine: "Those are not all whole numbers, so multiply by {k}: {whole}",
  molecularLine:
    "{molar} g/mol ÷ {empirical} g/mol = {multiple} → multiply every subscript by {n}",
  closeCall:
    "The closest fit is {deviation} from a whole number. That is acceptable for rounded data, but check the values if you expected an exact match.",
  notWhole:
    "The molar mass is {multiple} times the empirical formula mass, which is not close to a whole number. Check the molar mass or the data.",
  percentSum:
    "The percentages add to {sum}, not 100. If an element is missing, it can be found by difference.",
  copy: "Copy formulas",
  copied: "Copied!",
  copyText: "Empirical formula: {empirical}; molecular formula: {molecular}",
  enterRows: "Enter at least two elements with their percentages or masses.",
  incompleteRow: "Row {number} needs both an element symbol and a value.",
  badValue: "The value for {symbol} must be a number greater than zero.",
  errors: {
    tooFew: "Enter at least two elements.",
    unknownElement: "“{symbol}” is not an element symbol.",
    duplicateElement:
      "{symbol} appears twice. Combine its values into one row.",
    badMass: "The value for {symbol} must be a number greater than zero.",
    noRatio:
      "No whole-number ratio fits within the chosen tolerance (multipliers up to 8 were tried). Check the data, or choose a looser tolerance.",
    badInput: "Enter positive masses for the sample, the CO₂ and the H₂O.",
    exceedsSample:
      "The carbon and hydrogen in the products weigh more than the sample. Check the masses.",
    noRoomForOxygen:
      "The carbon and hydrogen already account for the whole sample, so no oxygen is left. Untick the oxygen option.",
    invalidMolarMass: "The molar mass must be a number greater than zero.",
  },
} as const;

type Strings = typeof EMPIRICAL_FORMULA_CALCULATOR_STRINGS;
type Mode = "masses" | "combustion";
type ValueKind = "percent" | "grams";

interface Row {
  id: number;
  symbol: string;
  value: string;
}

const TOLERANCES = ["0.05", "0.1", "0.2"] as const;
const MAX_ROWS = 8;
const PERCENT_SLACK = 1;
const PERCENT_TOTAL = 100;

function normaliseSymbol(raw: string): string {
  const text = raw.trim();
  return text === "" ? "" : text[0].toUpperCase() + text.slice(1).toLowerCase();
}

function errorText(strings: Strings, error: EmpiricalError): string {
  const symbol = "symbol" in error ? error.symbol : "";
  return t(strings.errors[error.code], { symbol });
}

/** Rows to element masses, or the message that says why they cannot be read. */
function readRows(
  rows: readonly Row[],
  strings: Strings,
  fmt: (n: number) => string
): ElementMass[] | string {
  const out: ElementMass[] = [];
  for (const [index, row] of rows.entries()) {
    const symbol = normaliseSymbol(row.symbol);
    if (symbol === "" && row.value.trim() === "") {
      continue;
    }
    if (symbol === "" || row.value.trim() === "") {
      return t(strings.incompleteRow, { number: fmt(index + 1) });
    }
    const mass = parseMeasuredNumber(row.value);
    if (mass === null || !(mass > 0)) {
      return t(strings.badValue, { symbol });
    }
    out.push({ symbol, mass });
  }
  return out;
}

export default function EmpiricalFormulaCalculatorWidget() {
  const strings = useToolUi(EMPIRICAL_FORMULA_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("masses");
  const [kind, setKind] = useState<ValueKind>("percent");
  const [rows, setRows] = useState<Row[]>(() =>
    strings.exampleSymbols.map((symbol, index) => ({
      id: index + 1,
      symbol,
      value: strings.exampleValues[index] ?? "",
    }))
  );
  const [nextId, setNextId] = useState(strings.exampleSymbols.length + 1);
  const [sample, setSample] = useState("1.000");
  const [co2, setCo2] = useState("1.911");
  const [water, setWater] = useState("1.173");
  const [rest, setRest] = useState(true);
  const [molarText, setMolarText] = useState("");
  const [tolerance, setTolerance] =
    useState<(typeof TOLERANCES)[number]>("0.1");

  const update = (id: number, patch: Partial<Row>) =>
    setRows(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const body = (() => {
    let masses: ElementMass[];
    if (mode === "masses") {
      const read = readRows(rows, strings, fmt);
      if (typeof read === "string") {
        return <ErrorNotice>{read}</ErrorNotice>;
      }
      if (read.length < 2) {
        return <Notice>{strings.enterRows}</Notice>;
      }
      masses = read;
    } else {
      const found = combustionMasses(
        parseMeasuredNumber(sample) ?? Number.NaN,
        parseMeasuredNumber(co2) ?? Number.NaN,
        parseMeasuredNumber(water) ?? Number.NaN,
        rest
      );
      if ("code" in found) {
        return <ErrorNotice>{strings.errors[found.code]}</ErrorNotice>;
      }
      masses = [
        { symbol: "C", mass: found.carbon },
        { symbol: "H", mass: found.hydrogen },
        ...(found.oxygen === null ? [] : [{ symbol: "O", mass: found.oxygen }]),
      ].filter((m) => m.mass > 0);
    }
    const result = empiricalFromMasses(masses, Number(tolerance));
    if ("code" in result) {
      return <ErrorNotice>{errorText(strings, result)}</ErrorNotice>;
    }
    return renderResult(result, masses);
  })();

  function renderResult(result: EmpiricalResult, masses: ElementMass[]) {
    const molarValue =
      molarText.trim() === "" ? null : parseMeasuredNumber(molarText);
    if (molarText.trim() !== "" && !(molarValue !== null && molarValue > 0)) {
      return <ErrorNotice>{strings.errors.invalidMolarMass}</ErrorNotice>;
    }
    const molecular =
      molarValue === null
        ? null
        : molecularMultiple(molarValue, result.formulaMass);
    const sum = masses.reduce((total, m) => total + m.mass, 0);
    const showSum =
      mode === "masses" &&
      kind === "percent" &&
      Math.abs(sum - PERCENT_TOTAL) > PERCENT_SLACK;
    return (
      <>
        {showSum ? (
          <Notice>
            {t(strings.percentSum, { sum: fmt(Math.round(sum * 100) / 100) })}
          </Notice>
        ) : null}
        <EmpiricalResults
          fmt={fmt}
          molarMass={molarValue}
          molecular={molecular}
          result={result}
          strings={strings}
          valueHeading={
            mode === "masses" && kind === "percent"
              ? strings.columnPercent
              : strings.columnGrams
          }
        />
      </>
    );
  }

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="ef-mode"
        onChange={setMode}
        options={[
          { value: "masses", label: strings.modeMasses },
          { value: "combustion", label: strings.modeCombustion },
        ]}
        value={mode}
      />
      {mode === "masses" ? (
        <div className="space-y-4">
          <PillGroup
            label={strings.valuesAre}
            name="ef-kind"
            onChange={setKind}
            options={[
              { value: "percent", label: strings.valuesPercent },
              { value: "grams", label: strings.valuesGrams },
            ]}
            value={kind}
          />
          <p className={LABEL}>{strings.rowsHeading}</p>
          {rows.map((row, index) => (
            <div
              className="grid grid-cols-[1fr_2fr_auto] items-end gap-3"
              key={row.id}
            >
              <TextField
                id={`ef-symbol-${row.id}`}
                label={t(strings.symbolLabel, { number: fmt(index + 1) })}
                mono
                onChange={(symbol) => update(row.id, { symbol })}
                value={row.symbol}
              />
              <TextField
                id={`ef-value-${row.id}`}
                label={t(strings.valueLabel, { number: fmt(index + 1) })}
                numeric
                onChange={(value) => update(row.id, { value })}
                value={row.value}
              />
              <button
                aria-label={t(strings.removeRow, { number: fmt(index + 1) })}
                className={BUTTON_SMALL}
                disabled={rows.length <= 2}
                onClick={() => setRows(rows.filter((r) => r.id !== row.id))}
                type="button"
              >
                ×
              </button>
            </div>
          ))}
          <button
            className={BUTTON_SECONDARY}
            disabled={rows.length >= MAX_ROWS}
            onClick={() => {
              setRows([...rows, { id: nextId, symbol: "", value: "" }]);
              setNextId(nextId + 1);
            }}
            type="button"
          >
            {strings.addRow}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <TextField
              id="ef-sample"
              label={strings.sampleLabel}
              numeric
              onChange={setSample}
              value={sample}
            />
            <TextField
              id="ef-co2"
              label={strings.co2Label}
              numeric
              onChange={setCo2}
              value={co2}
            />
            <TextField
              id="ef-water"
              label={strings.waterLabel}
              numeric
              onChange={setWater}
              value={water}
            />
          </div>
          <Checkbox
            checked={rest}
            label={strings.restIsOxygen}
            onChange={setRest}
          />
          <p className="text-muted-foreground text-xs">
            {strings.combustionHint}
          </p>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.molarMassHint}
          id="ef-molar"
          label={strings.molarMassLabel}
          numeric
          onChange={setMolarText}
          value={molarText}
        />
        <SelectField
          id="ef-tolerance"
          label={strings.toleranceLabel}
          onChange={setTolerance}
          options={[
            {
              value: "0.05",
              label: t(strings.toleranceStrict, { value: fmt(0.05) }),
            },
            {
              value: "0.1",
              label: t(strings.toleranceStandard, { value: fmt(0.1) }),
            },
            {
              value: "0.2",
              label: t(strings.toleranceLoose, { value: fmt(0.2) }),
            },
          ]}
          value={tolerance}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
